<template>
  <div
    :class="[
      'Checkbox',
      `Checkbox--${size}`,
      { 'is-error': hasError }
    ]"
  >
    <Label
      :id="`${id}-label`"
      :for="id"
      class="Checkbox-inputWrapper"
    >
      <CheckboxRoot
        :id="id"
        v-model="checked"
        class="Checkbox-box"
        :disabled="disabled"
        :required="isRequired"
        :aria-labelledby="`${id}-label`"
      >
        <CheckboxIndicator class="Checkbox-indicator" asChild>
          <Icon icon="check" :size="iconSize" />
        </CheckboxIndicator>
      </CheckboxRoot>

      <span class="Checkbox-label" v-html="label" />
    </Label>

    <div v-if="description" :id="`${id}-description`" class="Checkbox-description">
      {{ description }}
    </div>

    <FieldHelper
      :errorMessage="errorMessage"
      :helperText="helperText"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, useAttrs, useId } from 'vue';
import { useFieldValidation, type FieldProps } from '@cvo/ui/composables';
import { CheckboxRoot, CheckboxIndicator, Label } from 'reka-ui';
import Icon from '../Icon/Icon.vue';
import FieldHelper from '../FieldHelper/FieldHelper.vue';

export interface CheckboxProps extends FieldProps {
  description?: string;
  size?: 'sm' | 'md' | 'lg';
}

const props = withDefaults(defineProps<CheckboxProps>(), {
  size: 'md',
  error: undefined,
});

const checked = defineModel<boolean | null>({
  /**
   * default value is set to null so that error
   * compute validation doesn't result in error text
   * rendering on mount
   */
  default: null,
});

const { hasError, errorMessage, isRequired } = useFieldValidation(props);

const id = computed<string>(() => {
  return useAttrs().id as string | undefined ?? useId();
});

const iconSize = computed(() => {
  return props.size === 'lg' ? 'md' : props.size;
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Checkbox {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: vars.$dimensions-d-2;

    // Wrapper for checkbox box + label
    &-inputWrapper {
      display: flex;
      gap: vars.$dimensions-d-2;
      align-items: start;
      cursor: pointer;
    }

    // Actual checkbox "box"
    &-box {
      position: relative;
      flex-shrink: 0;
      margin-top: 0.1em;

      display: flex;
      align-items: center;
      justify-content: center;

      border-width: vars.$atoms-controls-checkbox-box-border-stroke;
      border-style: solid;
      border-color: vars.$atoms-controls-checkbox-default-off-box-border-color;
      border-radius: vars.$atoms-controls-checkbox-box-border-radius;
      background-color: vars.$atoms-controls-checkbox-default-off-box-bg-color;
      overflow: hidden;
      cursor: inherit;

      @include layout.square(20px);

      .Checkbox-indicator {
        color: vars.$atoms-controls-checkbox-default-off-box-icon-color;
      }

      &:focus-visible {
        outline: 2px solid vars.$color-brand-primary-900;
        outline-offset: 2px;
      }
    }

    &-label {
      line-height: 1.4;
      color: vars.$atoms-controls-checkbox-default-off-primary-text-color;
      @include text.text('md');
    }

    &-description {
      text-align: start;
      line-height: 1.4;
      color: vars.$atoms-controls-checkbox-default-off-secondary-text-color;
      @include text.text('sm');
    }

    &:has(.Checkbox-box[data-state='checked']) {
      .Checkbox-box {
        border-color: vars.$atoms-controls-checkbox-default-on-box-border-color;
        background-color: vars.$atoms-controls-checkbox-default-on-box-bg-color;

        .Checkbox-indicator {
          color: vars.$atoms-controls-checkbox-default-on-box-icon-color;
        }
      }

      .Checkbox-label {
        color: vars.$atoms-controls-checkbox-default-on-primary-text-color;
      }

      .Checkbox-description {
        color: vars.$atoms-controls-checkbox-default-on-secondary-text-color;
      }
    }

    &.is-error {
      .Checkbox-box {
        border-color: vars.$color-system-status-danger-900;
      }
    }

    // --- Disabled Styles ---
    &:has(.Checkbox-box[data-disabled]) {
      .Checkbox-inputWrapper {
        cursor: not-allowed;
      }

      .Checkbox-box {
        border-color: vars.$atoms-controls-checkbox-disabled-off-box-border-color;
        background-color: vars.$atoms-controls-checkbox-disabled-off-box-bg-color;

        .Checkbox-indicator {
          color: vars.$atoms-controls-checkbox-disabled-off-box-icon-color;
        }
      }

      .Checkbox-label {
        color: vars.$atoms-controls-checkbox-disabled-off-primary-text-color;
      }

      .Checkbox-description {
        color: vars.$atoms-controls-checkbox-disabled-off-secondary-text-color;
      }
    }

    // --- Disabled + Checked Styles ---
    &:has(.Checkbox-box[data-disabled][data-state='checked']) {
      .Checkbox-box {
        border-color: vars.$atoms-controls-checkbox-disabled-on-box-border-color;
        background-color: vars.$atoms-controls-checkbox-disabled-on-box-bg-color;

        .Checkbox-indicator {
          color: vars.$atoms-controls-checkbox-disabled-on-box-icon-color;
        }
      }

      .Checkbox-label {
        color: vars.$atoms-controls-checkbox-disabled-on-primary-text-color;
      }

      .Checkbox-description {
        color: vars.$atoms-controls-checkbox-disabled-on-secondary-text-color;
      }
    }

    // Size variations
    &--sm {
      display: flex;
      gap: vars.$dimensions-d-1;
      flex-wrap: wrap;

      .Checkbox-inputWrapper {
        gap: vars.$dimensions-d-1;
      }

      .Checkbox-box {
        @include layout.square(16px);
      }

      .Checkbox-label {
        @include text.text('sm');
      }

      .Checkbox-description {
        @include text.text('xs');
      }
    }
  }
}
</style>